import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, ArrowRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../lib/axios";

// ── The analytical tabs ──────────────────────────────────────────────────────
//
// BD Performance, Sales Pipeline, Projects & Revenue and Forecasting. Each one
// reads the filters it is handed, so a period chosen in the workspace applies
// here without being restated.
//
// Two display rules run through all four, and both come from the backend
// deliberately returning null:
//   null is a dash, never a zero. "Nothing closed" and "lost everything" are
//   different reports and must not look the same.
//   Forecasts live in their own styling with their assumptions attached, and
//   are never placed beside actuals as though they were comparable.

export const money = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `$${Number(n).toLocaleString()}`;
export const count = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : Number(n).toLocaleString();
export const pct = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `${n}%`;

export const useTabData = (path: string, filters: Record<string, string>, active: boolean) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active) return;
    let live = true;
    setLoading(true);
    setError("");
    axiosInstance
      .get(`/reports/${path}`, { params: filters })
      .then(({ data }) => live && setData(data))
      .catch((e) => live && setError(e?.response?.data?.message || "Could not build this report."))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [path, active, JSON.stringify(filters)]);

  return { data, loading, error };
};

export const Panel = ({ title, sub, tone, children }: any) => (
  <div className={`rounded-2xl border bg-white p-4 ${tone === "violet" ? "border-violet-200" : "border-slate-200"}`}>
    <p className="text-[13px] font-bold text-slate-800">{title}</p>
    {sub && <p className="text-[11px] text-slate-500 mb-2.5">{sub}</p>}
    {children}
  </div>
);

export const Stat = ({ label, value, sub, tone = "slate", onClick }: any) => {
  const tones: Record<string, string> = {
    slate: "text-slate-800", emerald: "text-emerald-600", rose: "text-rose-600",
    amber: "text-amber-600", violet: "text-violet-700",
  };
  const Wrap: any = onClick ? "button" : "div";
  return (
    <Wrap
      {...(onClick ? { type: "button", onClick } : {})}
      className={`text-left rounded-xl border border-slate-200 bg-white p-3.5 ${onClick ? "hover:border-[#2462b2]/40 hover:shadow-sm transition group" : ""}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-[20px] font-extrabold mt-0.5 ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
      {onClick && (
        <span className="text-[10px] text-[#2462b2] opacity-0 group-hover:opacity-100 flex items-center gap-0.5 mt-1">
          Open <ArrowRight size={9} />
        </span>
      )}
    </Wrap>
  );
};

const Bars = ({ rows, colour = "bg-[#2462b2]", format = count }: any) => {
  const max = Math.max(...rows.map((r: any) => r.value || 0), 1);
  if (!rows.length) return <p className="text-[12px] text-slate-400">Nothing to show.</p>;
  return (
    <div className="space-y-1.5">
      {rows.map((r: any) => (
        <div key={r.label}>
          <div className="flex justify-between text-[12px]">
            <span className="text-slate-600 truncate pr-2">{r.label}</span>
            <span className="font-bold text-slate-800 shrink-0">{format(r.value)}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
            <div className={`h-full ${colour} rounded-full`} style={{ width: `${((r.value || 0) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Shell = ({ loading, error, data, children }: any) => {
  if (loading && !data) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-10 flex justify-center"><Loader2 size={20} className="animate-spin text-slate-400" /></div>;
  }
  if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-800">{error}</div>;
  if (!data) return null;
  return children;
};

// ── BD Performance ───────────────────────────────────────────────────────────
export const BdPerformanceTab = ({ filters, active }: any) => {
  const { data, loading, error } = useTabData("bd-performance", filters, active);
  const navigate = useNavigate();

  return (
    <Shell loading={loading} error={error} data={data}>
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Bids" value={count(data.team.bids)} />
            <Stat label="Proposals" value={count(data.team.proposals)} />
            <Stat label="Deals won" value={count(data.team.wonDeals)} tone="emerald" sub={`${count(data.team.lostDeals)} lost`} />
            <Stat label="Win rate" value={pct(data.team.winRate)} sub="closed deals only" />
            <Stat label="Revenue" value={money(data.team.revenue)} tone="emerald" />
          </div>

          <Panel title="By Business Developer" sub="Sorted by revenue. Totals are summed, never averaged.">
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] min-w-[760px]">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    {["BD", "Bids", "Connects", "Proposals", "Won", "Win rate", "Projects", "Revenue", "Overdue"].map((h) => (
                      <th key={h} className="pb-2 pr-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.rows.map((r: any) => (
                    <tr key={r.id} className="hover:bg-slate-50/60">
                      <td className="py-2 pr-3 font-bold text-slate-800">{r.name}</td>
                      <td className="py-2 pr-3">{count(r.bids)}</td>
                      <td className="py-2 pr-3">{count(r.connects)}</td>
                      <td className="py-2 pr-3">{count(r.proposals)}</td>
                      <td className="py-2 pr-3 text-emerald-700 font-semibold">{count(r.wonDeals)}</td>
                      <td className="py-2 pr-3">{pct(r.winRate)}</td>
                      <td className="py-2 pr-3">{count(r.projects)}</td>
                      <td className="py-2 pr-3 font-bold">{money(r.revenue)}</td>
                      <td className={`py-2 pr-3 ${r.followUps.overdue > 0 ? "text-rose-600 font-bold" : "text-slate-400"}`}>
                        {count(r.followUps.overdue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {data.rows.some((r: any) => r.targets?.length > 0) && (
            <Panel title="Target achievement" sub="Current cycle, per person">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.rows.flatMap((r: any) =>
                  (r.targets || []).map((t: any) => (
                    <div key={`${r.id}-${t.metric}`} className={`rounded-xl border p-3 ${t.behind ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
                      <p className="text-[11px] font-bold text-slate-600">{r.name} · {t.metricLabel}</p>
                      <p className="text-[16px] font-extrabold text-slate-900">
                        {t.unit === "currency" ? money(t.achieved) : count(t.achieved)}
                        <span className="text-[12px] text-slate-400"> / {t.unit === "currency" ? money(t.target) : count(t.target)}</span>
                      </p>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                        <div className={`h-full rounded-full ${t.behind ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, t.pct ?? 0)}%` }} />
                      </div>
                      <p className="text-[10.5px] text-slate-500 mt-1">{t.periodLabel} · {t.status}</p>
                    </div>
                  ))
                )}
              </div>
            </Panel>
          )}

          <button type="button" onClick={() => navigate("/bids")} className="text-[12px] text-[#2462b2] font-semibold hover:underline">
            Open the bid records →
          </button>
        </div>
      )}
    </Shell>
  );
};

// ── Sales Pipeline ───────────────────────────────────────────────────────────
export const PipelineTab = ({ filters, active }: any) => {
  const { data, loading, error } = useTabData("pipeline", filters, active);
  const navigate = useNavigate();

  return (
    <Shell loading={loading} error={error} data={data}>
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="In negotiation" value={count(data.kpis.negotiating)} tone="amber" sub={money(data.kpis.openValue)} onClick={() => navigate("/contacts")} />
            <Stat label="Won" value={count(data.kpis.won)} tone="emerald" sub={money(data.kpis.wonValue)} onClick={() => navigate("/contacts")} />
            <Stat label="Lost" value={count(data.kpis.lost)} tone="rose" sub={money(data.kpis.lostValue)} onClick={() => navigate("/contacts")} />
            <Stat label="Win rate" value={pct(data.kpis.winRate)} sub="closed deals only" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Avg time to close" value={data.kpis.avgDaysToClose === null ? "—" : `${data.kpis.avgDaysToClose}d`} />
            <Stat label="Stalled" value={count(data.kpis.stalled)} tone="amber" sub={`untouched ${data.stalledAfterDays}+ days`} />
            <Stat label="Open with a value" value={`${count(data.kpis.valuedOpen)} / ${count(data.kpis.negotiating)}`} sub="the rest have no project yet" />
            <Stat label="Won with a value" value={`${count(data.kpis.valuedWon)} / ${count(data.kpis.won)}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Panel title="Where the pipeline came from" sub="By acquisition source">
              <Bars rows={data.bySource} />
            </Panel>

            <Panel title="Stalled opportunities" sub={`Open but untouched for over ${data.stalledAfterDays} days`}>
              {data.stalledDeals.length === 0 ? (
                <p className="text-[12px] text-slate-400">Nothing has gone quiet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {data.stalledDeals.slice(0, 8).map((d: any) => (
                    <li key={d.id} className="flex items-center justify-between text-[12px]">
                      <span className="text-slate-700 truncate pr-2">{d.name}{d.source ? ` · ${d.source}` : ""}</span>
                      <span className="text-amber-700 font-semibold shrink-0 flex items-center gap-1">
                        <Clock size={10} /> {d.daysSinceUpdate}d
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          {/* Said plainly: the money figures cover only the deals that have a
              project behind them. Without this the totals read as the whole
              pipeline. */}
          {data.kpis.valuedOpen < data.kpis.negotiating && (
            <p className="text-[11.5px] text-amber-700 flex items-start gap-1.5">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              Only {data.kpis.valuedOpen} of {data.kpis.negotiating} open opportunities have a project behind them, so the
              open value covers those alone.
            </p>
          )}
        </div>
      )}
    </Shell>
  );
};

// ── Projects & Revenue ───────────────────────────────────────────────────────
export const ProjectsRevenueTab = ({ filters, active }: any) => {
  const { data, loading, error } = useTabData("projects-revenue", filters, active);
  const navigate = useNavigate();

  return (
    <Shell loading={loading} error={error} data={data}>
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Projects created" value={count(data.projects.created)} sub={money(data.projects.createdValue)} onClick={() => navigate("/projects")} />
            <Stat label="Invoiced" value={money(data.revenue.invoiced)} />
            <Stat label="Paid" value={money(data.revenue.paid)} tone="emerald" />
            <Stat label="Outstanding" value={money(data.revenue.outstanding)} tone="amber" />
            <Stat label="Overdue" value={money(data.revenue.overdue)} tone="rose" onClick={() => navigate("/admin/invoices")} />
          </div>

          {/* The five money figures above are different quantities. Said once,
              here, rather than leaving a reader to assume they add up. */}
          <p className="text-[11px] text-slate-500">
            Project value is what was agreed · invoiced is what was billed · paid is what arrived. They are not
            added together.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Panel title="Projects by status" sub={`${count(data.projects.total)} in total · ${count(data.projects.upcoming)} starting soon`}>
              <Bars rows={data.projects.byStatus.map((s: any) => ({ label: s.status, value: s.count }))} />
            </Panel>
            <Panel title="Revenue by source">
              <Bars rows={data.revenue.bySource} colour="bg-emerald-500" format={money} />
            </Panel>
            <Panel title="Revenue by category">
              <Bars rows={data.revenue.byCategory.slice(0, 6)} colour="bg-violet-500" format={money} />
            </Panel>
          </div>

          <Panel title="Billing trend" sub="Six months — invoiced against paid">
            <div className="flex items-end gap-2 h-28">
              {data.revenue.trend.map((t: any) => {
                const max = Math.max(...data.revenue.trend.map((x: any) => x.invoiced), 1);
                return (
                  <div key={t.label} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end gap-0.5 h-full" title={`${t.label}: invoiced ${money(t.invoiced)}, paid ${money(t.paid)}`}>
                      <div className="flex-1 bg-slate-300 rounded-t" style={{ height: `${(t.invoiced / max) * 100}%` }} />
                      <div className="flex-1 bg-emerald-500 rounded-t" style={{ height: `${(t.paid / max) * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-500">{t.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[10.5px] text-slate-400 mt-1.5">Grey is invoiced · green is paid</p>
          </Panel>

          {data.revenue.byBd.length > 1 && (
            <Panel title="Revenue by Business Developer">
              <Bars rows={data.revenue.byBd} colour="bg-[#2462b2]" format={money} />
            </Panel>
          )}
        </div>
      )}
    </Shell>
  );
};

// ── Forecasting ──────────────────────────────────────────────────────────────
export const ForecastingTab = ({ filters, active }: any) => {
  const { data, loading, error } = useTabData("forecasting", filters, active);

  const Unavailable = ({ reason }: { reason: string }) => (
    <p className="text-[12px] text-slate-500 flex items-start gap-1.5">
      <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-500" />
      {reason}
    </p>
  );

  return (
    <Shell loading={loading} error={error} data={data}>
      {data && (
        <div className="space-y-3">
          {/* Everything on this tab is an estimate. Said once at the top and
              repeated in the styling, so no figure here is mistaken for a
              result that has already happened. */}
          <div className="rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-2.5">
            <p className="text-[12px] font-bold text-violet-800">These are estimates, not booked results.</p>
            <p className="text-[11.5px] text-violet-700">
              Each one shows what it assumes. Where there is not enough history to support a figure, it says so
              rather than producing a precise-looking guess.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Panel title="Closing forecast" tone="violet" sub="Weighted pipeline">
              {data.closing.available ? (
                <>
                  <p className="text-[24px] font-extrabold text-violet-700">{money(data.closing.weightedPipeline)}</p>
                  <ul className="mt-2 space-y-0.5">
                    {(data.closing.assumptions || []).map((a: string, i: number) => (
                      <li key={i} className="text-[11px] text-slate-500 pl-2.5 relative">
                        <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-slate-300" />{a}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10.5px] font-semibold text-violet-700 mt-2">Confidence: {data.closing.confidence}</p>
                </>
              ) : <Unavailable reason={data.closing.reason} />}
            </Panel>

            <Panel title="Project forecast" tone="violet" sub="Expected new projects">
              {data.projects.available ? (
                <>
                  <p className="text-[24px] font-extrabold text-violet-700">{data.projects.expectedProjects}</p>
                  <ul className="mt-2 space-y-0.5">
                    {(data.projects.assumptions || []).map((a: string, i: number) => (
                      <li key={i} className="text-[11px] text-slate-500 pl-2.5 relative">
                        <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-slate-300" />{a}
                      </li>
                    ))}
                  </ul>
                </>
              ) : <Unavailable reason={data.projects.reason} />}
            </Panel>

            <Panel title="Revenue forecast" tone="violet">
              {data.revenue.available ? (
                <>
                  <p className="text-[24px] font-extrabold text-violet-700">{money(data.revenue.expected)}</p>
                  <p className="text-[11px] text-slate-500 mt-1.5">{data.revenue.basis}</p>
                  <p className="text-[10.5px] font-semibold text-violet-700 mt-2">Confidence: {data.revenue.confidence}</p>
                </>
              ) : <Unavailable reason={data.revenue.reason} />}
            </Panel>
          </div>

          <Panel title="Target achievement forecast" sub="Where the current cycle lands if nothing changes">
            {data.targets.length === 0 ? (
              <p className="text-[12px] text-slate-400">
                No targets are set for this scope. Choose a single BD to see their cycle projected.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {data.targets.map((t: any) => (
                  <div key={t.metric} className={`rounded-xl border p-3 ${t.willMeet === false ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{t.metricLabel}</p>
                    {t.projectedEnd === null ? (
                      <p className="text-[12px] text-slate-500 mt-1">{t.reason}</p>
                    ) : (
                      <>
                        <p className="text-[18px] font-extrabold text-slate-900">
                          {t.unit === "currency" ? money(t.projectedEnd) : count(t.projectedEnd)}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          projected · target {t.unit === "currency" ? money(t.target) : count(t.target)}
                        </p>
                        <p className={`text-[11px] font-semibold mt-1 ${t.willMeet ? "text-emerald-700" : "text-amber-700"}`}>
                          {t.willMeet ? "On course to meet it" : `Short by ${t.unit === "currency" ? money(t.gap) : count(t.gap)}`}
                        </p>
                      </>
                    )}
                    <p className="text-[10.5px] text-slate-400 mt-1">{t.periodLabel} · {t.pacePct}% elapsed</p>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </Shell>
  );
};
